import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { BACKEND, errList, errMsg, publicApi, readableText } from "@/lib/api";
import { AddressStep } from "./AddressStep";
import { MeasureTool } from "./MeasureTool";
import { ContactStep, EstimateStep, ManualForm } from "./Forms";
import { resetScroll } from "@/lib/mobileView";
import { trackPublic } from "@/lib/track";

type Stage = "property" | "contact" | "measure" | "estimate" | "success" | "manual";
const STEPS = ["Property", "Contact", "Measure", "Estimate"];

function Stepper({ stage }: { stage: Stage }) {
  const idx = { property: 0, contact: 1, measure: 2, estimate: 3 }[stage as string] ?? -1;
  if (idx < 0) return null;
  return (
    <ol className="grid grid-cols-4 gap-1.5 mb-5" aria-label="Progress" data-testid="widget-stepper">
      {STEPS.map((s, i) => (
        <li key={s} className="text-[11px] font-medium" aria-current={i === idx ? "step" : undefined}>
          <div className={`h-1.5 rounded-full mb-1.5 ${i <= idx ? "brand-btn" : "bg-slate-200"}`} />
          <span className={i === idx ? "text-slate-900" : "text-slate-400"}>{i + 1}. {s}</span>
        </li>
      ))}
    </ol>
  );
}

export function EmbedPage() {
  const { publicId } = useParams();
  const [q] = useSearchParams();
  return <Widget publicId={publicId as string} source={q.get("preview") ? "preview" : "embed"} embedded />;
}

export function HostedPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [pid, setPid] = useState<string | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    publicApi.get(`/public/hosted/${slug}`).then((r) => {
      setPid(r.data.public_id);
      // An old (renamed) link: show the business's current link in the address bar.
      if (r.data.slug && r.data.slug !== slug) { nav(`/q/${r.data.slug}${window.location.search}`, { replace: true }); }
    }).catch((e) => setError(errMsg(e)));
  }, [slug, nav]);
  if (error) return <div className="min-h-screen grid place-items-center p-6 text-center" data-testid="hosted-error">{error}</div>;
  if (!pid) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  return <div className="min-h-[100dvh] bg-slate-100 sm:py-8"><Widget publicId={pid} source="hosted" /></div>;
}

function Widget({ publicId, source, embedded }: { publicId: string; source: string; embedded?: boolean }) {
  const root = useRef<HTMLDivElement>(null);
  const [cfg, setCfg] = useState<any>(null);
  const [loadErr, setLoadErr] = useState("");
  const [stage, setStage] = useState<Stage>("property");
  const [lead, setLead] = useState<any>(null);
  const [mapView, setMapView] = useState<any>(null);
  const [manualDone, setManualDone] = useState<string | null>(null);
  const [service, setService] = useState<any>(null);
  const [address, setAddress] = useState<any>(null);
  const [shapes, setShapes] = useState<any[]>([]);
  const [est, setEst] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [geoErrors, setGeoErrors] = useState<string[]>([]);
  const [contactDraft, setContactDraft] = useState<any>(null);
  const firstStage = useRef(true);
  useEffect(() => {
    if (firstStage.current) { firstStage.current = false; return; }
    resetScroll(root.current);
  }, [stage]);
  const [notice, setNotice] = useState("");
  const [confirmation, setConfirmation] = useState("");

  useEffect(() => {
    publicApi.get(`/public/widgets/${publicId}/config`).then((r) => {
      setCfg(r.data);
      document.title = `${r.data.business_name} | Instant Estimate`;
      if (!r.data.maps.browser_key) { setReason("maps_not_configured"); setStage("manual"); }
    }).catch((e) => setLoadErr(errMsg(e)));
  }, [publicId]);
  useEffect(() => {
    if (!embedded || !root.current || window.parent === window) return;
    const ro = new ResizeObserver(() => window.parent.postMessage({ type: "iqw:resize", height: root.current?.scrollHeight }, "*"));
    ro.observe(root.current);
    return () => ro.disconnect();
  }, [embedded, cfg]);

  const goManual = (r: string | null) => { setReason(r); setStage("manual"); setNotice(""); };
  // Address selections happen before a session exists; queue them and report once it does.
  const pendingSelects = useRef(0);
  const placeSelected = () => {
    if (session?.session_token) trackPublic(publicId, session.session_token, "place_select");
    else pendingSelects.current += 1;
  };
  useEffect(() => {
    if (!session?.session_token) return;
    for (; pendingSelects.current > 0; pendingSelects.current -= 1) trackPublic(publicId, session.session_token, "place_select");
  }, [session, publicId]);
  const ensureSession = async (purpose: "map" | "manual") => {
    if (session && (purpose === "manual" || session.maps_allowed)) return session;
    const origin = document.referrer || (window.location as any).ancestorOrigins?.[0] || null;
    const { data } = await publicApi.post(`/public/widgets/${publicId}/session`, { purpose, source, embed_origin: origin });
    setSession(data);
    return data;
  };
  const finishManual = (r: string) => { setManualDone(r || "manual"); setStage("success"); };
  const propertyDone = async (a: any, s: any) => {
    setAddress(a); setService(s); setShapes([]); setMapView(null); setNotice("");
    try {
      const sess = await ensureSession("map");
      if (!sess.maps_allowed) return goManual(sess.maps_unavailable_reason);
      setStage("contact");
    } catch (e) { setNotice(errMsg(e)); }
  };
  const startLead = async (contact: any) => {
    const tok = session.session_token;
    const { data } = await publicApi.post(`/public/widgets/${publicId}/leads/start`, { ...contact, session_token: tok, service_id: service.id, address });
    setLead(data); setConfirmation(data.confirmation_id);
    if (data.next === "manual_review") return finishManual(data.reason);
    const m = await publicApi.post(`/public/widgets/${publicId}/leads/${data.lead_id}/measure`, { session_token: tok });
    if (!m.data.maps_allowed) return finishManual(m.data.reason);
    setStage("measure");
  };
  const estimate = async (storeFeatures: any[], features: any[]) => {
    setShapes(storeFeatures); setBusy(true); setGeoErrors([]);
    try {
      const { data } = await publicApi.post(`/public/widgets/${publicId}/leads/${lead.lead_id}/estimate`, { session_token: session.session_token, features });
      if (data.next === "manual_review") finishManual(data.reason);
      else { setEst(data); setStage("estimate"); }
    } catch (e) {
      setGeoErrors(errList(e).length ? errList(e) : [errMsg(e)]);
    } finally { setBusy(false); }
  };
  const requestService = async () => {
    await publicApi.post(`/public/widgets/${publicId}/leads/${lead.lead_id}/request-service`, { session_token: session.session_token });
    setManualDone(null); setStage("success");
  };
  const submitManual = async (body: any) => {
    const sess = await ensureSession("manual");
    const { data } = await publicApi.post(`/public/widgets/${publicId}/manual-leads`, { ...body, session_token: sess.session_token });
    setConfirmation(data.confirmation_id); setManualDone("manual"); setStage("success");
  };

  if (loadErr) return <div className="p-8 text-center text-slate-700" data-testid="widget-load-error">{loadErr}</div>;
  if (!cfg) return <div className="p-8 text-center text-slate-400" data-testid="widget-loading">Loading…</div>;
  const style = { "--brand": cfg.primary_color || "#0F766E", "--brand-fg": readableText(cfg.primary_color) } as React.CSSProperties;
  const accepting = cfg.availability.accepting;

  return (
    <div ref={root} style={style} className={`bg-white text-slate-900 font-sans ${embedded ? "" : "max-w-xl mx-auto sm:rounded-2xl sm:shadow-lg sm:border"}`} data-testid="quote-widget">
      <header className="flex items-center gap-3 px-5 pt-5 pb-4 border-b">
        {cfg.logo_url ? <img src={`${BACKEND}${cfg.logo_url}`} alt={cfg.business_name} className="h-10 max-w-[140px] object-contain" data-testid="widget-logo" /> : null}
        <div className="min-w-0">
          <p className="font-heading font-bold leading-tight truncate" style={{ color: cfg.secondary_color || undefined }} data-testid="widget-business-name">{cfg.business_name}</p>
          <p className="text-xs text-slate-500">{cfg.headline}</p>
        </div>
      </header>
      <div className="p-5">
        {!accepting ? (
          <div className="py-8 text-center space-y-2" data-testid="widget-unavailable">
            <p className="font-semibold">Online quotes are not available right now.</p>
            <p className="text-sm text-slate-600">Please contact {cfg.business_name} directly{cfg.contact.phone ? <> at <a className="underline" href={`tel:${cfg.contact.phone}`}>{cfg.contact.phone}</a></> : null}.</p>
            {cfg.contact.website && <a className="text-sm underline brand-text" href={cfg.contact.website} target="_blank" rel="noreferrer">{cfg.contact.website}</a>}
          </div>
        ) : (
          <>
            <Stepper stage={stage} />
            {notice && <p className="mb-4 text-sm rounded-lg bg-amber-50 border border-amber-200 p-3 text-amber-900" role="alert" data-testid="widget-notice">{notice}</p>}
            {stage === "property" && <AddressStep cfg={cfg} value={address} service={service} onPlaceSelect={placeSelected} onFail={goManual} onManual={() => goManual(null)} onConfirm={propertyDone} />}
            {stage === "contact" && <ContactStep cfg={cfg} draft={contactDraft} onDraft={setContactDraft} onBack={() => setStage("property")} onSubmit={startLead} />}
            {stage === "measure" && <MeasureTool cfg={cfg} unit={service.pricing_unit} address={address} initial={shapes} view={mapView} onView={setMapView} onMapLoad={() => trackPublic(publicId!, session?.session_token, "map_load")} busy={busy} errors={geoErrors} onEdit={() => setGeoErrors((e) => (e.length ? [] : e))} onFail={() => finishManual("maps_unavailable")} onSubmit={estimate} />}
            {stage === "estimate" && est && <EstimateStep est={est} onEdit={() => setStage("measure")} onRequest={requestService} />}
            {stage === "manual" && <ManualForm cfg={cfg} service={service} address={address} reason={reason} onSubmit={submitManual} onBack={cfg.maps.browser_key ? () => setStage("property") : undefined} />}
            {stage === "success" && (
              <div className="py-8 text-center space-y-3 fade-up" data-testid="widget-success">
                <CheckCircle2 className="h-12 w-12 mx-auto brand-text" />
                <h2 className="font-heading text-2xl font-bold">{manualDone ? "Thanks — we have your details" : "Service requested"}</h2>
                <p className="text-sm text-slate-600 max-w-sm mx-auto" data-testid="success-message">{manualDone
                  ? `${cfg.business_name} will review your property and contact you with a quote. Nothing has been booked or charged.`
                  : `${cfg.business_name} received your service request and will contact you to confirm details and scheduling. Nothing has been booked or charged.`}</p>
                <p className="text-xs text-slate-500">Reference: <span className="font-mono" data-testid="confirmation-id">{confirmation}</span></p>
              </div>
            )}
          </>
        )}
      </div>
      <footer className="px-5 pb-5 pt-3 border-t text-[11px] text-slate-500 space-y-1">
        <p>{cfg.disclaimer}</p>
        {cfg.footer_note && <p>{cfg.footer_note}</p>}
        <p className="flex flex-wrap gap-x-3">
          {cfg.privacy_url && <a href={cfg.privacy_url} target="_blank" rel="noreferrer" className="underline">{cfg.business_name} Privacy</a>}
          {cfg.terms_url && <a href={cfg.terms_url} target="_blank" rel="noreferrer" className="underline">{cfg.business_name} Terms</a>}
          <Link to="/privacy" target="_blank" className="underline">Widget Privacy</Link>
          <Link to="/terms" target="_blank" className="underline">Terms</Link>
          <span>Powered by Instant Quote Wizard</span>
        </p>
      </footer>
    </div>
  );
}
